import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { useAutoSave } from '@/hooks/useAutoSave';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { parseDoseCombination } from './dose-utils';
import { DecimalInput } from '@/components/ui/decimal-input';
import { Loader2, Save, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import type { ClinicianResponse } from './assessment/types';
import { SaveStateChip } from './assessment/SaveStateChip';
import { DoseCombobox } from './assessment/DoseCombobox';
import { useAssessmentSubmit } from './assessment/useAssessmentSubmit';

interface ClinicalData {
  tsh: number | null;
  gestational_week: number | null;
  weight: number | null;
  age: number | null;
  on_treatment: boolean;
  previous_dose: number | null;
  thyroidectomy: boolean;
  thyroidectomy_reason: 'benign' | 'cancer' | null;
  hypothyroidism_before: boolean;
  miscarriage: boolean;
  ivf: boolean;
  twin: boolean;
  fT4: number | null;
  TAI: boolean;
  tai_handling_choice_naive_unknown: string | null;
  optional_treatment_choice: string | null;
  medications: {
    iron_supplements: boolean;
    calcium_supplements: boolean;
  };
}

export const ClinicalAssessment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [startTime] = useState(Date.now());
  const { submit, submitting, submitPhase } = useAssessmentSubmit<ClinicalData>({
    arm: 'pregnancy',
    tables: {
      cases: 'clinical_cases',
      clinicianResponses: 'clinician_responses',
      apiResponses: 'api_responses',
      concordanceResults: 'concordance_results',
    },
    railwayUrl: import.meta.env.VITE_RAILWAY_PREGNANCY_URL as string,
    buildCaseInsert: (d) => ({
      tsh: d.tsh,
      gestational_week: d.gestational_week,
      weight: d.weight,
      age: d.age,
      on_treatment: d.on_treatment,
      previous_dose: d.previous_dose,
      thyroidectomy: d.thyroidectomy,
      hypothyroidism_before: d.hypothyroidism_before,
      miscarriage: d.miscarriage,
      ivf: d.ivf,
      twin: d.twin,
      ft4: d.fT4,
      tai: d.TAI,
      tai_handling_choice_naive_unknown: d.tai_handling_choice_naive_unknown,
      optional_treatment_choice: d.optional_treatment_choice,
      iron_supplements: d.medications.iron_supplements,
      calcium_supplements: d.medications.calcium_supplements,
    }),
    buildRailwayPayload: (d) => ({ ...d }),
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [clinicalData, setClinicalData] = useState<ClinicalData>({
    tsh: null,
    gestational_week: null,
    weight: null,
    age: null,
    on_treatment: false,
    previous_dose: null,
    thyroidectomy: false,
    thyroidectomy_reason: null,
    hypothyroidism_before: false,
    miscarriage: false,
    ivf: false,
    twin: false,
    fT4: null,
    TAI: false,
    tai_handling_choice_naive_unknown: null,
    optional_treatment_choice: null,
    medications: {
      iron_supplements: false,
      calcium_supplements: false,
    },
  });

  const [clinicianResponse, setClinicianResponse] = useState<ClinicianResponse>({
    recommended_dose: '',
    treatment_recommendation: '',
    previous_dose: '',
    notes: '',
  });

  // Auto-save functionality
  const combinedFormData = { clinicalData, clinicianResponse };
  const { loadFromStorage, clearSavedData, getSavedDataAge, lastSavedAt } = useAutoSave({
    key: `clinical_assessment_${user?.id}`,
    data: combinedFormData,
    delay: 8000, // Increased from 3 seconds to 8 seconds - less frequent saves
    enabled: !submitting && hasUnsavedChanges,
  });

  // Load saved data on component mount
  useEffect(() => {
    const savedData = loadFromStorage();
    if (savedData && savedData.clinicalData && savedData.clinicianResponse) {
      const savedAge = getSavedDataAge();
      if (savedAge) {
        // Only show notification if data is substantial (not just empty form)
        const hasSubstantialData =
          (savedData.clinicalData.tsh ?? 0) > 0 ||
          (savedData.clinicalData.gestational_week ?? 0) > 0 ||
          (savedData.clinicalData.weight ?? 0) > 0 ||
          (savedData.clinicalData.age ?? 0) > 0 ||
          savedData.clinicianResponse.recommended_dose !== '' ||
          savedData.clinicianResponse.notes !== '';
          
        if (hasSubstantialData) {
          toast({
            title: 'Βρέθηκαν αποθηκευμένα δεδομένα',
            description: `Τελευταία αποθήκευση: ${savedAge.toLocaleString('el-GR')}`,
            duration: 4000,
          });
        }
        
        setClinicalData(savedData.clinicalData);
        setClinicianResponse(savedData.clinicianResponse);
      }
    }
  }, []);

  // Track unsaved changes - ONLY if form has meaningful data
  useEffect(() => {
    // Check if form has ANY meaningful data
    const hasSubstantialData =
      (clinicalData.tsh ?? 0) > 0 ||
      (clinicalData.gestational_week ?? 0) > 0 ||
      (clinicalData.weight ?? 0) > 0 ||
      (clinicalData.age ?? 0) > 0 ||
      clinicalData.on_treatment ||
      clinicalData.thyroidectomy ||
      clinicalData.TAI ||
      clinicalData.ivf ||
      clinicalData.twin ||
      clinicalData.miscarriage ||
      clinicalData.fT4 !== null ||
      clinicianResponse.recommended_dose !== '' ||
      clinicianResponse.treatment_recommendation !== '' ||
      clinicianResponse.notes !== '';
    
    // Only set unsaved changes if there's actual data AND we're not submitting
    if (hasSubstantialData && !submitting) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [clinicalData, clinicianResponse, submitting]);

  useUnsavedChangesGuard(hasUnsavedChanges);

  // Form validation. Required numeric fields are null when empty.
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (clinicalData.tsh === null) {
      errors.tsh = 'Η TSH είναι υποχρεωτική';
    } else if (clinicalData.tsh < 0 || clinicalData.tsh > 100) {
      errors.tsh = 'Η TSH πρέπει να είναι μεταξύ 0-100';
    }

    if (clinicalData.gestational_week === null) {
      errors.gestational_week = 'Η εβδομάδα κύησης είναι υποχρεωτική';
    } else if (clinicalData.gestational_week < 1 || clinicalData.gestational_week > 42) {
      errors.gestational_week = 'Οι εβδομάδες κύησης πρέπει να είναι μεταξύ 1-42';
    }

    if (clinicalData.weight === null) {
      errors.weight = 'Το βάρος είναι υποχρεωτικό';
    } else if (clinicalData.weight < 30 || clinicalData.weight > 150) {
      errors.weight = 'Το βάρος πρέπει να είναι μεταξύ 30-150 kg';
    }

    if (clinicalData.age === null) {
      errors.age = 'Η ηλικία είναι υποχρεωτική';
    } else if (clinicalData.age < 18 || clinicalData.age > 50) {
      errors.age = 'Η ηλικία πρέπει να είναι μεταξύ 18-50 ετών';
    }

    if (clinicalData.on_treatment && !clinicalData.previous_dose) {
      errors.previous_dose = 'Η προηγούμενη δόση είναι υποχρεωτική όταν η ασθενής λαμβάνει θεραπεία';
    }

    if (clinicalData.thyroidectomy && !clinicalData.thyroidectomy_reason) {
      errors.thyroidectomy_reason = 'Επιλέξτε την αιτία θυρεοειδεκτομής (καλοήθης ή καρκίνος)';
    }

    if (!clinicianResponse.recommended_dose) {
      errors.recommended_dose = 'Η προτεινόμενη δοσολογία είναι υποχρεωτική';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!validateForm()) {
      toast({
        variant: 'destructive',
        title: 'Σφάλματα φόρμας',
        description: 'Παρακαλώ διορθώστε τα σφάλματα πριν συνεχίσετε.',
      });
      return;
    }

    // Out-of-scope gate (D1 / RSK-PREG-20): thyroid-cancer cases require deliberate
    // TSH suppression, the opposite of the pregnancy target — they are not enrolled.
    // Block here so no case row is ever created (the backend also declines it).
    if (clinicalData.thyroidectomy && clinicalData.thyroidectomy_reason === 'cancer') {
      toast({
        variant: 'destructive',
        title: 'Εκτός πεδίου μελέτης',
        description: 'Η ασθενής δεν είναι δυνατό να ενταχθεί στη μελέτη.',
        duration: 9000,
      });
      return;
    }

    await submit(clinicalData, clinicianResponse, {
      startTime,
      clearDraft: () => {
        clearSavedData();
        setHasUnsavedChanges(false);
      },
    });
  };

  // Helper function to get field error
  const getFieldError = (fieldName: string) => formErrors[fieldName];
  
  // Helper function to check if field has error
  const hasFieldError = (fieldName: string) => !!formErrors[fieldName];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8f7f2' }}>
      <Card className="w-full max-w-4xl mx-auto shadow-lg">
        <CardHeader style={{ backgroundColor: '#F3E5E0' }} className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold text-gray-800">
                Φόρμα εκτίμησης Ειδικού Ενδοκρινολόγου
              </CardTitle>
              <p className="text-sm text-gray-800 mt-1">
                Κλινική μελέτη συμφωνίας αποφάσεων
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {hasUnsavedChanges && !submitting && (
                <Badge variant="outline" className="text-orange-600 border-orange-200">
                  <Clock className="w-3 h-3 mr-1" />
                  Μη αποθηκευμένα
                </Badge>
              )}
              {submitting && (
                <Badge variant="outline" className="text-blue-600 border-blue-200">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Επεξεργασία...
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      <CardContent>
        {/* Error Summary */}
        {Object.keys(formErrors).length > 0 && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <div className="font-medium mb-2">Παρακαλώ διορθώστε τα παρακάτω σφάλματα:</div>
              <ul className="list-disc list-inside space-y-1">
                {Object.entries(formErrors).map(([field, error]) => (
                  <li key={field} className="text-sm">{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Required Fields */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold text-white shrink-0" style={{ backgroundColor: 'var(--ink)' }}>1</span>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--ink)' }}>Υποχρεωτικά Πεδία</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="tsh" className="text-sm font-medium">
                  TSH (0-100) <span className="text-red-500">*</span>
                </Label>
                <DecimalInput
                  id="tsh"
                  value={clinicalData.tsh}
                  onValueChange={(v) => setClinicalData({ ...clinicalData, tsh: v })}
                  placeholder="για παράδειγμα, 3.2 ή 3,2"
                  className={hasFieldError('tsh') ? 'border-red-500 focus:border-red-500' : ''}
                  required
                />
                {hasFieldError('tsh') && (
                  <div className="flex items-center space-x-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>{getFieldError('tsh')}</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="gestational_week" className="text-sm font-medium">
                  Εβδομάδα Κύησης (1-42) <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="gestational_week"
                    type="number"
                    min="1"
                    max="42"
                    value={clinicalData.gestational_week ?? ''}
                    onChange={(e) => setClinicalData({
                      ...clinicalData,
                      gestational_week: e.target.value === '' ? null : parseInt(e.target.value),
                    })}
                    className={`font-mono tabular-nums ${hasFieldError('gestational_week') ? 'border-red-500 focus:border-red-500' : ''}`}
                    required
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs text-gray-500">
                    {clinicalData.gestational_week !== null && clinicalData.gestational_week > 0 ? (
                      clinicalData.gestational_week <= 13 ? '1ο τρίμηνο' :
                      clinicalData.gestational_week <= 27 ? '2ο τρίμηνο' : '3ο τρίμηνο'
                    ) : ''}
                  </div>
                </div>
                {hasFieldError('gestational_week') && (
                  <div className="flex items-center space-x-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>{getFieldError('gestational_week')}</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="weight" className="text-sm font-medium">
                  Βάρος kg (30-150) <span className="text-red-500">*</span>
                </Label>
                <DecimalInput
                  id="weight"
                  value={clinicalData.weight}
                  onValueChange={(v) => setClinicalData({ ...clinicalData, weight: v })}
                  placeholder="για παράδειγμα, 65.5 ή 65,5"
                  className={hasFieldError('weight') ? 'border-red-500 focus:border-red-500 transition-colors' : 'transition-colors'}
                  required
                />
                {hasFieldError('weight') && (
                  <div className="flex items-center space-x-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>{getFieldError('weight')}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div className="space-y-2">
                <Label htmlFor="ft4">fT4</Label>
                <DecimalInput
                  id="ft4"
                  value={clinicalData.fT4}
                  onValueChange={(v) => setClinicalData({ ...clinicalData, fT4: v })}
                  placeholder="για παράδειγμα, 1.2 ή 1,2"
                />
              </div>
              
              <div className="space-y-2" style={{ gridColumn: 'span 2' }}>
                <Label htmlFor="age" className="text-sm font-medium">
                  Ηλικία (18-50) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="age"
                  type="number"
                  min="18"
                  max="50"
                  value={clinicalData.age ?? ''}
                  onChange={(e) => setClinicalData({
                    ...clinicalData,
                    age: e.target.value === '' ? null : parseInt(e.target.value),
                  })}
                  className={`font-mono tabular-nums ${hasFieldError('age') ? 'border-red-500 focus:border-red-500 transition-colors' : 'transition-colors'}`}
                  required
                />
                {hasFieldError('age') && (
                  <div className="flex items-center space-x-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>{getFieldError('age')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Optional Fields */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="on_treatment"
                checked={clinicalData.on_treatment}
                onCheckedChange={(checked) => {
                  const isOnTreatment = !!checked;
                  setClinicalData({
                    ...clinicalData, 
                    on_treatment: isOnTreatment,
                    // Restore original logic for hypothyroidism_before
                    hypothyroidism_before: isOnTreatment ? true : clinicalData.hypothyroidism_before,
                    // Add correct logic to reset previous_dose if unchecked
                    previous_dose: isOnTreatment ? clinicalData.previous_dose : null
                  });
                   // Also reset the UI dropdown if unchecked
                  if (!isOnTreatment) {
                      setClinicianResponse(prev => ({ ...prev, previous_dose: '' }));
                  }
                }}
              />
              <Label htmlFor="on_treatment" className="text-red-600">Ήδη υπό αγωγή με λεβοθυροξίνη</Label>
            </div>

            {clinicalData.on_treatment && (
              <div className="space-y-2">
                <Label htmlFor="previous_dose" className="text-sm font-medium">
                  Προηγούμενη Δόση - Previous Dose <span className="text-red-500">*</span>
                </Label>
                <DoseCombobox
                  value={clinicianResponse.previous_dose}
                  hasError={hasFieldError('previous_dose')}
                  placeholder="Επίλεξε την προηγούμενη δόση"
                  excludeNoTreatment
                  onChange={(value) => {
                    setClinicianResponse({ ...clinicianResponse, previous_dose: value });
                    setClinicalData({
                      ...clinicalData,
                      previous_dose: value ? parseDoseCombination(value).dailyAverage : null,
                    });
                  }}
                />
                {hasFieldError('previous_dose') && (
                  <div className="flex items-center space-x-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>{getFieldError('previous_dose')}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div className="h-4 bg-white rounded"></div>
              <h3 className="text-sm font-medium">Αιτία υποθυρεοειδισμού</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { key: 'TAI', label: 'Hashimoto' },
                  { key: 'hypothyroidism_before', label: 'Διεγνωσμένος Υποθυρεοειδισμός' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={clinicalData[key as keyof ClinicalData] as boolean}
                      onCheckedChange={(checked) => setClinicalData({...clinicalData, [key]: !!checked})}
                    />
                    <Label htmlFor={key}>{label}</Label>
                  </div>
                ))}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="thyroidectomy"
                    checked={clinicalData.thyroidectomy}
                    onCheckedChange={(checked) => setClinicalData({
                      ...clinicalData,
                      thyroidectomy: !!checked,
                      // Clear the reason when unchecked so it can't linger.
                      thyroidectomy_reason: checked ? clinicalData.thyroidectomy_reason : null,
                    })}
                  />
                  <Label htmlFor="thyroidectomy">Θυρεοειδεκτομή</Label>
                </div>
              </div>

              {clinicalData.thyroidectomy && (
                <div className="space-y-2 rounded-md border p-3 bg-gray-50">
                  <Label className="text-sm font-medium">
                    Αιτία θυρεοειδεκτομής <span className="text-red-500">*</span>
                  </Label>
                  <RadioGroup
                    value={clinicalData.thyroidectomy_reason ?? ''}
                    onValueChange={(v) => setClinicalData({ ...clinicalData, thyroidectomy_reason: v as 'benign' | 'cancer' })}
                    className="flex flex-col gap-2 sm:flex-row sm:gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="benign" id="tx-benign" />
                      <Label htmlFor="tx-benign">Καλοήθεια</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="cancer" id="tx-cancer" />
                      <Label htmlFor="tx-cancer">Καρκίνος θυρεοειδούς</Label>
                    </div>
                  </RadioGroup>
                  {hasFieldError('thyroidectomy_reason') && (
                    <div className="flex items-center space-x-1 text-sm text-red-600">
                      <AlertCircle className="w-4 h-4" />
                      <span>{getFieldError('thyroidectomy_reason')}</span>
                    </div>
                  )}
                  {clinicalData.thyroidectomy_reason === 'cancer' && (
                    <Alert className="border-red-400 bg-red-100">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-800 font-medium">
                        Εκτός πεδίου μελέτης - η ασθενής δεν είναι δυνατό να ενταχθεί στη μελέτη
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium">Ιστορικό/Πλαίσιο (που θα μπορούσε να επηρεάζει τη δόση της λεβοθυροξίνης)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { key: 'miscarriage', label: 'Ιστορικό αποβολής' },
                  { key: 'ivf', label: 'IVF - κύηση υψηλής' },
                  { key: 'twin', label: 'Πολύδυμη Κύηση' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={clinicalData[key as keyof ClinicalData] as boolean}
                      onCheckedChange={(checked) => setClinicalData({...clinicalData, [key]: !!checked})}
                    />
                    <Label htmlFor={key}>{label}</Label>
                  </div>
                ))}
              </div>
            </div>

              <div className="space-y-4">
              <h3 className="text-sm font-medium">Αγωγή (που θα μπορούσε να επηρεάζει την απορρόφηση της λεβοθυροξίνης)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'iron_supplements', label: 'Συμπληρώματα Fe σιδήρου' },
                  { key: 'calcium_supplements', label: 'Συμπληρώματα Ca ασβέστιου' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={clinicalData.medications[key as keyof typeof clinicalData.medications]}
                      onCheckedChange={(checked) => setClinicalData({
                        ...clinicalData,
                        medications: { ...clinicalData.medications, [key]: !!checked }
                      })}
                    />
                    <Label htmlFor={key}>{label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Clinician Response */}
          <div className="space-y-4 border-t pt-12">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold text-white shrink-0" style={{ backgroundColor: 'var(--brand)' }}>2</span>
              <h3 className="text-xl font-bold" style={{ color: 'var(--brand)' }}>Η σύσταση του Ειδικού Ενδοκρινολόγου</h3>
            </div>
            <p className="text-base">Παρακαλώ συμπληρώστε την κύρια και την εναλλακτική δοσολογική σας πρόταση</p>
            
            <div className="space-y-2">
              <Label htmlFor="recommended_dose" className="text-sm font-medium">
                Προτεινόμενη Δοσολογία <span className="text-red-500">*</span>
              </Label>
              <DoseCombobox
                value={clinicianResponse.recommended_dose}
                hasError={hasFieldError('recommended_dose')}
                placeholder="Πληκτρολογήστε ή επιλέξτε δοσολογία..."
                onChange={(value) => setClinicianResponse({ ...clinicianResponse, recommended_dose: value })}
              />
              {hasFieldError('recommended_dose') && (
                <div className="flex items-center space-x-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>{getFieldError('recommended_dose')}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="recommended_dose_alternative">Εναλλακτική δοσολογική πρόταση</Label>
              <DoseCombobox
                value={clinicianResponse.treatment_recommendation}
                placeholder="Επίλεξε εναλλακτική δοσολογική πρόταση"
                allowClear
                onChange={(value) => setClinicianResponse({ ...clinicianResponse, treatment_recommendation: value })}
              />
            </div>

            <div className="space-y-2">
              <Textarea
                id="notes"
                value={clinicianResponse.notes}
                onChange={(e) => setClinicianResponse({...clinicianResponse, notes: e.target.value})}
                placeholder="Σημειώσεις σχόλια παρατηρήσεις..."
              />
            </div>
          </div>

          <Separator className="my-8" />
          
          <div className="flex items-center justify-between pt-6">
            <SaveStateChip
              isDirty={hasUnsavedChanges}
              submitting={submitting}
              lastSavedAt={lastSavedAt}
            />
            
            <Button 
              type="submit" 
              size="lg"
              disabled={submitting || !clinicianResponse.recommended_dose || (clinicalData.on_treatment && !clinicianResponse.previous_dose) || (clinicalData.thyroidectomy && (!clinicalData.thyroidectomy_reason || clinicalData.thyroidectomy_reason === 'cancer'))}
              className="text-white font-medium px-10 py-4 text-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#233038' }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {submitPhase === 'saving' && 'Αποθήκευση...'}
                  {submitPhase === 'scoring' && 'Σύσταση αλγορίθμου...'}
                  {submitPhase === 'reconciling' && 'Σύγκριση αποτελεσμάτων...'}
                  {submitPhase === 'idle' && 'Επεξεργασία...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Υποβολή Αξιολόγησης
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
    </div>
  );
};